/**
 * <bundle-upsell-popup>
 * ---------------------
 * Post-add-to-cart bundle upsell modal.
 *
 * - Opens when a bundle-eligible product (type + optional tag) is added (listens for
 *   the theme's `ajaxProduct:added` event fired by <product-form>).
 * - Lazily clones its inert grid <template> into the modal on first open.
 * - Each card's `is="product-bundle-form"` dispatches `bundle:added` on this element;
 *   we add that single item to the cart and refresh the progress bar + cart drawer.
 * - The cart drawer stays fully usable via the cart icon; we just suppress its
 *   auto-open for the triggering add so only the popup shows.
 */
if (!customElements.get('bundle-upsell-popup')) {
  customElements.define(
    'bundle-upsell-popup',
    class BundleUpsellPopup extends HTMLElement {
      connectedCallback() {
        this.gridReady = false;
        this.matchType = (this.dataset.matchType || '').trim().toLowerCase();
        this.restrictTag = (this.dataset.restrictTag || '').trim().toLowerCase();
        // Stop showing the popup once the cart holds this many qualifying items
        // (the bundle is full). 0 / unset = never suppress.
        this.hideAfterCount = parseInt(this.dataset.hideAfterCount, 10) || 0;
        this._tagCache = new Map();

        this.onProductAdded = this.onProductAdded.bind(this);
        this.onBundleAdded = this.onBundleAdded.bind(this);

        document.addEventListener('ajaxProduct:added', this.onProductAdded);
        this.addEventListener('bundle:added', this.onBundleAdded);

        // When the popup closes, stop suppressing the cart drawer so the cart icon
        // works normally afterward.
        this.modal?.addEventListener('modal:afterHide', () => {
          this._suppress = false;
          clearInterval(this._suppressTimer);
        });

        this.gotoCartButton?.addEventListener('click', () => this.goToCart());

        // Keep the "Go to cart" savings amount in sync with the cart.
        this.onCartUpdated = () => this.updateCtaSavings();
        document.addEventListener('cart:updated', this.onCartUpdated);
        if (window.theme && theme.pubsub && theme.pubsub.subscribe) {
          theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => this.updateCtaSavings());
        }
      }

      disconnectedCallback() {
        document.removeEventListener('ajaxProduct:added', this.onProductAdded);
        document.removeEventListener('cart:updated', this.onCartUpdated);
      }

      get modal() {
        return this._modal || (this._modal = this.querySelector('x-modal'));
      }

      get grid() {
        return this.querySelector('[data-bundle-upsell-grid]');
      }

      get template() {
        return this.querySelector('[data-bundle-upsell-grid-template]');
      }

      get errorEl() {
        return this.querySelector('[data-bundle-upsell-error]');
      }

      get gotoCartButton() {
        return this.querySelector('[data-bundle-upsell-gotocart]');
      }

      get cartDrawer() {
        return document.querySelector('cart-drawer');
      }

      /* ---------------------------------------------------------------- Trigger */

      async onProductAdded(event) {
        // Don't run on the cart page itself.
        if (document.body.classList.contains('template-cart')) return;

        const item = event?.detail?.product;
        if (!item) return;

        // Ignore adds that originate from inside this popup (we publish cartUpdate,
        // not ajaxProduct:added, but guard defensively).
        if (this.modal && this.modal.contains(event.target)) return;

        // Fast synchronous type gate. If the type can't match, bail immediately and
        // leave the cart drawer alone.
        const type = (item.product_type || '').trim().toLowerCase();
        if (this.matchType ? type !== this.matchType : !type) return;

        // This add is likely ours. <product-form> calls cartDrawer.show() right
        // after this event, so START suppressing the drawer NOW — before our async
        // eligibility/count checks let it settle open behind the popup. We release
        // (and reopen) it only if the add turns out ineligible.
        this.suppressCartDrawer();

        let eligible = await this.isEligible(item);

        // Bundle already full? Don't nag on further adds.
        if (eligible && this.hideAfterCount > 0) {
          const count = await this.qualifyingCount();
          if (count >= this.hideAfterCount) eligible = false;
        }

        if (!eligible) {
          this.releaseCartDrawer();
          return;
        }

        this.open();
      }

      // Tags for a product handle, cached (handle -> string[] of lowercased tags).
      async getTags(handle) {
        if (!handle) return [];
        if (this._tagCache.has(handle)) return this._tagCache.get(handle);
        let tags = [];
        try {
          const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
          if (res.ok) {
            const product = await res.json();
            tags = (product.tags || []).map((t) => String(t).trim().toLowerCase());
          }
        } catch (e) {
          tags = [];
        }
        this._tagCache.set(handle, tags);
        return tags;
      }

      async isEligible(item) {
        const type = (item.product_type || '').trim().toLowerCase();
        if (this.matchType && type !== this.matchType) return false;
        if (!this.matchType && !type) return false;

        // Optional tag gate (e.g. only "graphics" tees). The add.js response
        // doesn't include tags, so check the product JSON (cached).
        if (this.restrictTag) {
          const tags = await this.getTags(item.handle);
          if (!tags.includes(this.restrictTag)) return false;
        }
        return true;
      }

      // Count qualifying units currently in the cart — same matching the
      // progress bar uses (product_type + optional tag). Mirrors
      // bundle-progress-bar.js flattenMatchingItems.
      async qualifyingCount() {
        let cart;
        try {
          cart = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
        } catch (e) {
          return 0;
        }
        const items = (cart && cart.items) || [];
        let count = 0;
        for (const it of items) {
          const type = (it.product_type || '').trim().toLowerCase();
          if (this.matchType && type !== this.matchType) continue;
          if (this.restrictTag) {
            const tags = await this.getTags(it.handle);
            if (!tags.includes(this.restrictTag)) continue;
          }
          count += it.quantity || 1;
        }
        return count;
      }

      /* -------------------------------------------------- "Go to cart" savings */

      // Whole-cart savings shown under the Go-to-cart button. Summed from
      // line-level discounts (original_line_price - final_line_price), which
      // matches the cart drawer's "You saved" AND updates immediately after an
      // add — unlike cart.total_price, which can lag a beat while the automatic
      // discount recalculates. Always re-fetches /cart.js for freshness.
      async updateCtaSavings() {
        const wrap = this.querySelector('[data-bundle-savings]');
        const amt = this.querySelector('[data-bundle-savings-amount]');
        if (!wrap || !amt) return;
        let cart;
        try {
          cart = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
        } catch (e) {
          return;
        }
        const items = (cart && cart.items) || [];
        let cents = items.reduce((s, it) => s + Math.max(0, (it.original_line_price || 0) - (it.final_line_price || 0)), 0);
        // Fall back to cart-level totals if line fields are absent.
        if (cents === 0 && typeof cart.original_total_price === 'number') {
          cents = Math.max(0, cart.original_total_price - (cart.total_price || 0));
        }
        amt.textContent = '$' + (cents / 100).toFixed(2);
        wrap.toggleAttribute('hidden', cents <= 0);
      }

      /* ------------------------------------------------------------- Open/close */

      open() {
        // Keep the cart drawer suppressed for as long as the popup is open.
        this.suppressCartDrawer();
        this.ensureGrid();
        this.updateCtaSavings();

        if (this.modal && typeof this.modal.show === 'function') {
          this.modal.show();
        } else if (this.modal) {
          this.modal.setAttribute('open', '');
        }
      }

      // Persistently hide the cart drawer (it auto-opens on add). Runs a short
      // interval so it wins regardless of whether the drawer opens before or after
      // our async eligibility checks. releaseCartDrawer() stops + optionally reopens.
      suppressCartDrawer() {
        this._suppress = true;
        const drawer = this.cartDrawer;
        if (!drawer || typeof drawer.hide !== 'function') return;
        const hide = () => { if (this._suppress && drawer.open) drawer.hide(); };
        hide();
        requestAnimationFrame(hide);
        clearInterval(this._suppressTimer);
        let ticks = 0;
        this._suppressTimer = setInterval(() => {
          hide();
          if (++ticks > 16 || !this._suppress) clearInterval(this._suppressTimer);
        }, 50);
      }

      releaseCartDrawer() {
        this._suppress = false;
        clearInterval(this._suppressTimer);
        // We suppressed optimistically; this add wasn't ours, so restore the
        // drawer the shopper expected.
        const drawer = this.cartDrawer;
        if (drawer && typeof drawer.show === 'function' && !drawer.open) drawer.show();
      }

      ensureGrid() {
        if (this.gridReady) return;
        const template = this.template;
        const grid = this.grid;
        if (template && grid) {
          grid.appendChild(template.content.cloneNode(true));
        }
        this.gridReady = true;
      }

      goToCart() {
        // Stop suppressing first, then close the popup and open the cart drawer.
        this._suppress = false;
        clearInterval(this._suppressTimer);
        this.modal?.hide?.();
        const drawer = this.cartDrawer;
        if (drawer && typeof drawer.show === 'function') {
          drawer.show();
        } else {
          window.location.href = (window.theme && theme.routes && theme.routes.cart_url) || '/cart';
        }
      }

      /* -------------------------------------------------------- Per-card add */

      onBundleAdded(event) {
        const variant = event?.detail?.variant;
        const card = event?.detail?.product;
        if (!variant || !variant.id) return;

        const button = card ? card.querySelector('[type="submit"]') : null;
        this.addItem(variant.id, button);
      }

      addItem(variantId, button) {
        if (button) {
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('aria-busy', 'true');
        }
        this.setError(false);

        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const body = JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sectionsToBundle,
          sections_url: window.location.pathname,
        });

        fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then(async (parsed) => {
            if (parsed.status) {
              this.setError(parsed.description || parsed.message);
              return;
            }

            const cartJson = await (
              await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })
            ).json();
            cartJson.sections = parsed.sections;

            // Refresh the progress bar + cart drawer. We deliberately do NOT re-dispatch
            // ajaxProduct:added (that would re-trigger this popup).
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'bundle-upsell-popup',
              cart: cartJson,
            });
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cartJson } }));

            // Update the Go-to-cart savings directly (don't rely on event delivery).
            this.updateCtaSavings();
            this.flashAdded(button);
          })
          .catch(() => {
            this.setError(true);
          })
          .finally(() => {
            if (button) button.removeAttribute('aria-busy');
          });
      }

      flashAdded(button) {
        if (!button) return;
        const textEl = button.querySelector('.btn-text > span') || button.querySelector('.btn-text');
        if (!textEl) {
          button.removeAttribute('aria-disabled');
          return;
        }
        const original = this._addedRestore || textEl.textContent;
        textEl.textContent = '✓ Added';
        setTimeout(() => {
          textEl.textContent = original;
          button.removeAttribute('aria-disabled');
        }, 1400);
      }

      setError(message) {
        const el = this.errorEl;
        if (!el) return;
        el.toggleAttribute('hidden', !message);
        el.textContent = typeof message === 'string' ? message : '';
      }
    }
  );
}
